import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Pencil, Trash2, Sparkles } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@/stores/authStore';

interface TenantPrompt {
  id: string;
  entity: string;
  field: string;
  systemPrompt: string;
  userPromptTemplate: string;
  enabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

interface BuiltInPrompt {
  entity: string;
  field: string;
}

const PLACEHOLDER_HINTS = [
  '{{name}}',
  '{{firstName}}',
  '{{lastName}}',
  '{{accountName}}',
  '{{companyName}}',
  '{{website}}',
];

/**
 * Epic 89, Story 89.7 — Admin page for tenant-defined suggestion prompts.
 *
 * Lets tenant admins register / edit / disable per-tenant overrides of
 * the (entity, field) → prompt registry. Resolution order at lookup
 * time: tenant override wins; otherwise the in-code default; otherwise
 * 404.
 */
export function SuggestionPromptsAdminPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const [items, setItems] = useState<TenantPrompt[]>([]);
  const [builtIns, setBuiltIns] = useState<BuiltInPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<TenantPrompt | 'new' | null>(null);
  const [draftEntity, setDraftEntity] = useState('ACCOUNT');
  const [draftField, setDraftField] = useState('');
  const [draftSystem, setDraftSystem] = useState('');
  const [draftUser, setDraftUser] = useState('');
  const [draftEnabled, setDraftEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';

  function load() {
    setLoading(true);
    Promise.all([
      apiClient.get<TenantPrompt[]>('/admin/suggestion-prompts'),
      apiClient.get<BuiltInPrompt[]>('/admin/suggestion-prompts/built-ins'),
    ])
      .then(([listRes, builtRes]) => {
        setItems(listRes.data);
        setBuiltIns(builtRes.data);
      })
      .catch(() => {
        setItems([]);
        setBuiltIns([]);
      })
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function startNew() {
    setEditing('new');
    setDraftEntity('ACCOUNT');
    setDraftField('');
    setDraftSystem(
      'You are a B2B sales assistant. Fill in a single missing field. Be concise. Prefer null over guessing. Respond with valid JSON only.',
    );
    setDraftUser(
      'For {{name}}, suggest the value of the field. Respond with JSON only:\n{"value": "<value or null>", "confidence": 0-100, "rationale": "<one sentence>", "source": "training"}',
    );
    setDraftEnabled(true);
    setError(null);
  }

  function startEdit(p: TenantPrompt) {
    setEditing(p);
    setDraftEntity(p.entity);
    setDraftField(p.field);
    setDraftSystem(p.systemPrompt);
    setDraftUser(p.userPromptTemplate);
    setDraftEnabled(p.enabled);
    setError(null);
  }

  function cancel() {
    setEditing(null);
    setError(null);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/admin/suggestion-prompts', {
        entity: draftEntity.trim().toUpperCase(),
        field: draftField.trim(),
        systemPrompt: draftSystem.trim(),
        userPromptTemplate: draftUser,
        enabled: draftEnabled,
      });
      setEditing(null);
      load();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('suggestionPrompts.error_save'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(p: TenantPrompt) {
    if (!confirm(t('suggestionPrompts.confirm_delete', { entity: p.entity, field: p.field }))) return;
    try {
      await apiClient.delete(`/admin/suggestion-prompts/${p.id}`);
      load();
    } catch {
      setError(t('suggestionPrompts.error_delete'));
    }
  }

  if (!isAdmin) {
    return <p className="text-sm text-destructive">{t('common.access_denied')}</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('suggestionPrompts.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('suggestionPrompts.subtitle')}
          </p>
        </div>
        {!editing && (
          <Button size="sm" onClick={startNew}>
            {t('suggestionPrompts.new')}
          </Button>
        )}
      </div>

      {/* Built-ins informational panel */}
      <section className="rounded-lg border bg-muted/20 p-4 text-sm">
        <h3 className="mb-2 font-medium">{t('suggestionPrompts.builtins_title')}</h3>
        <p className="mb-2 text-xs text-muted-foreground">
          {t('suggestionPrompts.builtins_help')}
        </p>
        <div className="flex flex-wrap gap-2">
          {builtIns.map((b) => (
            <code
              key={`${b.entity}.${b.field}`}
              className="rounded bg-background px-2 py-1 text-xs"
            >
              {b.entity}.{b.field}
            </code>
          ))}
        </div>
      </section>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <form
          onSubmit={save}
          className="space-y-3 rounded-lg border bg-card p-4"
        >
          <h3 className="font-medium">
            {editing === 'new'
              ? t('suggestionPrompts.new_title')
              : t('suggestionPrompts.edit_title')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('suggestionPrompts.entity')}
              </label>
              <select
                value={draftEntity}
                onChange={(e) => setDraftEntity(e.target.value)}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                disabled={editing !== 'new'}
              >
                {['ACCOUNT', 'CONTACT', 'LEAD', 'OPPORTUNITY'].map((e) => (
                  <option key={e}>{e}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {t('suggestionPrompts.field')}
              </label>
              <Input
                value={draftField}
                onChange={(e) => setDraftField(e.target.value)}
                placeholder="website"
                disabled={editing !== 'new'}
                required
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('suggestionPrompts.system_prompt')}
            </label>
            <textarea
              value={draftSystem}
              onChange={(e) => setDraftSystem(e.target.value)}
              rows={3}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm font-mono"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t('suggestionPrompts.user_prompt_template')}
            </label>
            <textarea
              value={draftUser}
              onChange={(e) => setDraftUser(e.target.value)}
              rows={8}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm font-mono"
              required
            />
            <p className="text-xs text-muted-foreground">
              {t('suggestionPrompts.placeholder_help')}{' '}
              {PLACEHOLDER_HINTS.map((p) => (
                <code key={p} className="mx-1">{p}</code>
              ))}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="enabled"
              type="checkbox"
              checked={draftEnabled}
              onChange={(e) => setDraftEnabled(e.target.checked)}
            />
            <label htmlFor="enabled" className="text-sm">
              {t('suggestionPrompts.enabled')}
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={cancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </form>
      )}

      {/* Existing list */}
      <section className="rounded-lg border bg-card">
        {loading && (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('common.loading')}
          </p>
        )}
        {!loading && items.length === 0 && !editing && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            {t('suggestionPrompts.empty')}
          </div>
        )}
        <ul className="divide-y">
          {items.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <code className="rounded bg-muted px-1.5 py-0.5">
                    {p.entity}.{p.field}
                  </code>
                  {!p.enabled && (
                    <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {t('suggestionPrompts.disabled')}
                    </span>
                  )}
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {p.userPromptTemplate.slice(0, 200)}…
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => startEdit(p)}
                  className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  aria-label={t('common.edit')}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p)}
                  className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
